import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, Wrench, ArrowUpRight, ChevronRight } from "lucide-react";
import api from "../lib/api";
import Modal from "../components/Modal";
import VoiceTextarea from "../components/VoiceTextarea";
import ConvertFromButton from "../components/ConvertFromButton";

export default function Scheduler() {
  const [schedules, setSchedules] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [skipFor, setSkipFor] = useState(null); // schedule object being skipped
  const navigate = useNavigate();

  const load = async () => {
    const [s, e, c, st] = await Promise.all([
      api.get("/schedules"),
      api.get("/equipment"),
      api.get("/customers"),
      api.get("/sites"),
    ]);
    setSchedules(Array.isArray(s.data) ? s.data : []);
    setEquipment(Array.isArray(e.data) ? e.data : []);
    setCustomers(Array.isArray(c.data) ? c.data : []);
    setSites(Array.isArray(st.data) ? st.data : []);
  };
  useEffect(() => {
    load();
  }, []);

  const dispatch = async (id) => {
    const { data } = await api.post(`/schedules/${id}/create-jobcard`);
    navigate(`/jobcards/${data.id}`);
  };

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  // Merge classic /schedules with new equipment service items into one upcoming list
  const equipmentAsSchedules = equipment
    .filter((e) => e.is_service_item && e.next_service_date)
    .map((e) => ({
      id: `eq-${e.id}`,
      kind: "equipment",
      title: e.name,
      customer_id: e.customer_id,
      site_id: e.site_id,
      next_service_date: e.next_service_date,
      _equipment_id: e.id,
    }));
  const sortedSchedules = [...schedules, ...equipmentAsSchedules].sort(
    (a, b) => new Date(a.next_service_date) - new Date(b.next_service_date)
  );

  return (
    <div className="space-y-5" data-testid="scheduler-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Recurring services · view & dispatch</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            Service Schedule
          </h1>
          <p className="text-xs text-[#647087] mt-1">
            View what's coming up. To add or edit equipment / intervals, go to the customer's Equipment tab.
          </p>
        </div>
        <div className="flex gap-2" />
      </div>

      <UpcomingMiniCalendar
        schedules={sortedSchedules}
        equipment={equipment}
        customers={customers}
        sites={sites}
        onDispatch={dispatch}
      />

      {/* Wave 7-fix #8 — foldable time-bucket view, grouped by site within each */}
      <FoldableScheduleView
        schedules={sortedSchedules}
        customerName={customerName}
        siteName={siteName}
        onDispatch={dispatch}
        onSkip={(s) => setSkipFor(s)}
      />

      {/* Wave 8 — skip-with-reason modal */}
      <SkipScheduleModal
        target={skipFor}
        onClose={() => setSkipFor(null)}
        onDone={async () => {
          setSkipFor(null);
          await load();
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------
 * Foldable time-bucket schedule view — Wave 7-fix #8
 * Today / Tomorrow / This week / Next week / This month / Next month / Later
 * Inside each bucket, rows are grouped by Customer + Site for at-a-glance
 * "we're going there once for everything" visits.
 * ---------------------------------------------------------------------- */
function FoldableScheduleView({ schedules, customerName, siteName, onDispatch, onSkip }) {
  const [collapsed, setCollapsed] = useState({});

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(today); dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  const endOfWeek = new Date(today); endOfWeek.setDate(endOfWeek.getDate() + (7 - today.getDay()));
  const endOfNextWeek = new Date(endOfWeek); endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);

  const buckets = [
    { key: "overdue", label: "Overdue", color: "#F43F5E", match: (d) => d < today },
    { key: "today", label: "Today", color: "#1FB6C1", match: (d) => d >= today && d < tomorrow },
    { key: "tomorrow", label: "Tomorrow", color: "#06B6D4", match: (d) => d >= tomorrow && d < dayAfterTomorrow },
    { key: "this-week", label: "This week", color: "#A78BFA", match: (d) => d >= dayAfterTomorrow && d <= endOfWeek },
    { key: "next-week", label: "Next week", color: "#8B5CF6", match: (d) => d > endOfWeek && d <= endOfNextWeek },
    { key: "this-month", label: "This month", color: "#10B981", match: (d) => d > endOfNextWeek && d <= endOfMonth },
    { key: "next-month", label: "Next month", color: "#F59E0B", match: (d) => d > endOfMonth && d <= endOfNextMonth },
    { key: "later", label: "Later", color: "#647087", match: () => true },
  ];

  // Categorise each schedule. First-match wins.
  const inBucket = {};
  buckets.forEach((b) => { inBucket[b.key] = []; });
  schedules.forEach((s) => {
    const d = new Date(s.next_service_date);
    d.setHours(0, 0, 0, 0);
    const b = buckets.find((b) => b.match(d));
    if (b) inBucket[b.key].push(s);
  });

  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  const groupBySite = (items) => {
    const map = {};
    items.forEach((s) => {
      const k = `${s.customer_id}::${s.site_id}::${s.next_service_date?.slice(0, 10)}`;
      if (!map[k]) map[k] = { customer_id: s.customer_id, site_id: s.site_id, date: s.next_service_date, items: [] };
      map[k].items.push(s);
    });
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  return (
    <div className="space-y-3" data-testid="schedule-foldable-view">
      {buckets.map((b) => {
        const items = inBucket[b.key];
        if (items.length === 0) return null;
        const isCollapsed = b.key !== "overdue" && b.key !== "today" ? !!collapsed[b.key] : !!collapsed[b.key];
        const visits = groupBySite(items);
        return (
          <div key={b.key} className="hv-card overflow-hidden" data-testid={`schedule-bucket-${b.key}`}>
            <button
              type="button"
              onClick={() => toggle(b.key)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:brightness-125 transition"
              style={{ background: `${b.color}10`, borderBottom: isCollapsed ? "none" : `1px solid ${b.color}30` }}
            >
              <div className="flex items-center gap-2">
                <ChevronRight
                  className={`w-3.5 h-3.5 transition ${isCollapsed ? "" : "rotate-90"}`}
                  style={{ color: b.color }}
                />
                <span className="font-display text-base font-bold uppercase tracking-wider" style={{ color: b.color }}>
                  {b.label}
                </span>
              </div>
              <span
                className="text-[10px] font-mono px-2 py-0.5 rounded-full border"
                style={{ color: b.color, borderColor: `${b.color}55`, background: `${b.color}10` }}
              >
                {items.length} item{items.length !== 1 ? "s" : ""} · {visits.length} visit{visits.length !== 1 ? "s" : ""}
              </span>
            </button>
            {!isCollapsed && (
              <div className="p-3 space-y-2">
                {visits.map((v, i) => (
                  <div key={i} className="border border-white/5 rounded-md p-3" data-testid={`schedule-visit-${b.key}-${i}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">
                          {customerName(v.customer_id)} · <span className="text-[#a0aabf]">{siteName(v.site_id)}</span>
                        </div>
                        <div className="text-[11px] text-[#647087] mt-0.5">
                          {new Date(v.date).toLocaleDateString('en-GB')}
                          {v.items.length > 1 ? ` · bundle of ${v.items.length}` : ""}
                        </div>
                      </div>
                      {v.items.length > 1 && v.items[0].kind !== "equipment" && (
                        <button
                          onClick={() => v.items.forEach((s) => onDispatch(s.id))}
                          className="hv-btn hv-btn-primary text-xs"
                          data-testid={`schedule-dispatch-visit-${b.key}-${i}`}
                          title="Compile all items into a single site visit"
                        >
                          Dispatch visit ({v.items.length})
                        </button>
                      )}
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {v.items.map((s) => (
                        <li key={s.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-white/[0.02]" data-testid={`schedule-${s.id}`}>
                          <span className="flex-1 min-w-0 truncate">{s.title}</span>
                          {s.last_skip_reason && (
                            <span className="text-[10px] text-amber-300/80 italic">
                              skipped: {s.last_skip_reason.replaceAll("_", " ")}
                            </span>
                          )}
                          <ConvertFromButton
                            targets={["jobcard"]}
                            onConvert={() => onDispatch(s.id)}
                            variant="small-ghost"
                            testIdPrefix={`schedule-dispatch-${s.id}`}
                          />
                          {s.kind !== "equipment" && (
                            <button
                              onClick={() => onSkip(s)}
                              className="hv-btn hv-btn-ghost !px-2 !py-0.5 text-[10px]"
                              data-testid={`schedule-skip-${s.id}`}
                            >
                              Skip
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {schedules.length === 0 && (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">
          No schedules yet. Add equipment with a service interval on a customer's Equipment tab and they'll appear here.
        </div>
      )}
    </div>
  );
}

const SKIP_REASONS = [
  { v: "not_interested", label: "Customer not interested" },
  { v: "good_condition", label: "Equipment still in good condition" },
  { v: "no_stock", label: "Out of stock" },
  { v: "customer_deferred", label: "Customer asked to defer" },
  { v: "equipment_decommissioned", label: "Equipment decommissioned" },
  { v: "weather", label: "Weather / access issue" },
  { v: "other", label: "Other" },
];

function SkipScheduleModal({ target, onClose, onDone }) {
  const [reason, setReason] = useState("not_interested");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (target) {
      setReason("not_interested");
      setNotes("");
      setErr("");
    }
  }, [target]);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/schedules/${target.id}/skip`, { reason, notes });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not skip");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={`Skip: ${target.title}`}
      testId="skip-modal"
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} className="hv-btn hv-btn-primary text-xs" data-testid="skip-confirm">
            {busy ? "Saving…" : "Confirm skip"}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087]">
            Reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="skip-reason"
          >
            {SKIP_REASONS.map((r) => (
              <option key={r.v} value={r.v}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087]">
            Notes (optional)
          </label>
          <VoiceTextarea
            value={notes}
            onChange={setNotes}
            rows={3}
            placeholder="Anything specific the team should know…"
            data-testid="skip-notes"
           testIdPrefix="skip-notes-voice" context="note" />
        </div>
        <div className="text-[10px] text-[#647087]">
          Next service date will be pushed by one full period. You can edit it later.
        </div>
        {err && <div className="text-xs text-rose-300">{err}</div>}
      </div>
    </Modal>
  );
}


/* ------------- Mini calendar of upcoming services ------------- */
function UpcomingMiniCalendar({ schedules, equipment, customers, sites, onDispatch }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 28 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  const byDay = {};
  for (const s of schedules) {
    if (!s.next_service_date) continue;
    const d = new Date(s.next_service_date);
    d.setHours(0, 0, 0, 0);
    const k = d.toISOString().slice(0, 10);
    (byDay[k] = byDay[k] || []).push(s);
  }

  // Aggregate any overdue schedules into "today" cell so they're surfaced
  const overdue = schedules.filter((s) => {
    if (!s.next_service_date) return false;
    const d = new Date(s.next_service_date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  });
  if (overdue.length) {
    const todayKey = today.toISOString().slice(0, 10);
    byDay[todayKey] = [...overdue, ...(byDay[todayKey] || [])];
  }

  return (
    <div className="hv-card p-4" data-testid="scheduler-mini-cal">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-cyan-400" /> Next 28 days
        </h3>
        <div className="text-xs text-[#647087]">
          <span className="text-rose-300">●</span> overdue ·{" "}
          <span className="text-amber-300">●</span> due this week ·{" "}
          <span className="text-cyan-300">●</span> later
        </div>
      </div>
      {/* Iter 397 — Mobile calendar was collapsing 7-col grid into a
          vertical stack because each cell's min-width was pushing the
          layout over its container. On phones we now render a compact
          vertical list of upcoming days that have items; on tablet+
          we keep the 7-col monthly view. */}
      <div className="hidden sm:grid grid-cols-7 gap-1.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-[#647087] text-center pb-1">
            {d}
          </div>
        ))}
        {Array.from({ length: today.getDay() }, (_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {days.map((d, i) => {
          const k = d.toISOString().slice(0, 10);
          const items = byDay[k] || [];
          const isToday = i === 0;
          return (
            <div
              key={k}
              data-testid={`mini-cal-day-${k}`}
              className={`min-h-[60px] rounded-md border p-1.5 text-left transition ${
                isToday
                  ? "border-cyan-400/60 bg-cyan-400/5"
                  : items.length > 0
                  ? "border-white/10 bg-white/[0.02]"
                  : "border-white/5"
              }`}
            >
              <div className={`text-[10px] font-bold ${isToday ? "text-cyan-300" : "text-[#a0aabf]"}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5 mt-1">
                {items.slice(0, 2).map((s) => {
                  const next = new Date(s.next_service_date);
                  next.setHours(0, 0, 0, 0);
                  const overdueItem = next < today;
                  const soon = !overdueItem && (next - today) / 86400000 <= 7;
                  const c = overdueItem ? "#F43F5E" : soon ? "#F59E0B" : "#1FB6C1";
                  return (
                    <button
                      key={s.id}
                      onClick={() => s.kind !== "equipment" && onDispatch(s.id)}
                      className="block w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate transition hover:brightness-125"
                      style={{
                        background: `${c}22`,
                        color: c,
                        border: `1px solid ${c}55`,
                      }}
                      title={`${s.title} · ${customerName(s.customer_id)} · ${siteName(s.site_id)}${
                        s.kind === "equipment" ? " (equipment)" : " (click → create job)"
                      }`}
                    >
                      {s.title}
                    </button>
                  );
                })}
                {items.length > 2 && (
                  <div className="text-[9px] text-[#647087]">+{items.length - 2}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Iter 397 — Mobile list view of upcoming days that have items */}
      <div className="sm:hidden space-y-2">
        {days.map((d, i) => {
          const k = d.toISOString().slice(0, 10);
          const items = byDay[k] || [];
          if (items.length === 0) return null;
          const isToday = i === 0;
          const label = d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
          return (
            <div
              key={k}
              data-testid={`mini-cal-day-${k}`}
              className={`rounded-md border p-2 ${isToday ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-white/[0.02]"}`}
            >
              <div className={`text-[11px] uppercase tracking-wider font-bold mb-1 ${isToday ? "text-cyan-300" : "text-[#a0aabf]"}`}>
                {isToday ? "Today · " : ""}{label}
              </div>
              <div className="space-y-1">
                {items.map((s) => {
                  const next = new Date(s.next_service_date);
                  next.setHours(0, 0, 0, 0);
                  const overdueItem = next < today;
                  const soon = !overdueItem && (next - today) / 86400000 <= 7;
                  const c = overdueItem ? "#F43F5E" : soon ? "#F59E0B" : "#1FB6C1";
                  return (
                    <button
                      key={s.id}
                      onClick={() => s.kind !== "equipment" && onDispatch(s.id)}
                      className="block w-full text-left text-xs px-2 py-1.5 rounded transition"
                      style={{
                        background: `${c}22`,
                        color: c,
                        border: `1px solid ${c}55`,
                      }}
                    >
                      <div className="font-medium truncate">{s.title}</div>
                      <div className="text-[10px] opacity-80 truncate">
                        {customerName(s.customer_id)} · {siteName(s.site_id)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {days.every((d) => (byDay[d.toISOString().slice(0, 10)] || []).length === 0) && (
          <div className="text-center text-xs text-[#647087] py-6">
            No services scheduled in the next 28 days.
          </div>
        )}
      </div>
      {schedules.length === 0 && (
        <div className="text-xs text-[#647087] text-center pt-3">
          No upcoming services in the next 28 days.
        </div>
      )}
    </div>
  );
}
